import Layout from "@/components/shared/Layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from 'next/head';
import Image from 'next/image';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useState, useRef } from 'react';
import ImageSkeleton from '@/components/shared/ImageSkeleton';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { META_IMAGE, SITE_URL } from "@/utils/meta";
import WhatsappButton from '@/components/shared/WhatsappButton';
import SimilarBlogs from '@/components/blogs/SimilarBlogs';

const normalizeSectionContent = (content = '') =>
    content
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

export async function getServerSideProps({ locale, params, query }) {
    const { id } = params;
    const lang = query.lang || locale || "az";
    let blog = null;
    let allBlogs = [];

    try {
        const ngrokHeaders = { headers: { "ngrok-skip-browser-warning": "true" } };
        const [blogRes, allBlogsRes] = await Promise.all([
            fetch(`${process.env.NEXT_PUBLIC_API_URL}/blog/posts/${id}/?lang=${lang}`, ngrokHeaders),
            fetch(`${process.env.NEXT_PUBLIC_API_URL}/blog/posts/?lang=${lang}`, ngrokHeaders),
        ]);

        if (blogRes.ok) {
            blog = await blogRes.json();
        }
        if (allBlogsRes.ok) {
            const data = await allBlogsRes.json();
            allBlogs = Array.isArray(data) ? data : (data.results || []);
        }
    } catch (err) {
        // console.error("Failed to fetch blog:", err);
    }

    if (!blog) {
        return { notFound: true };
    }

    const similarBlogs = allBlogs.filter((b) => String(b.id) !== String(blog.id));

    return {
        props: {
            blog,
            similarBlogs,
            ...(await serverSideTranslations(locale || "az")),
        },
    };
}

const MediaImageWithSkeleton = ({ src, alt, rounded }) => {
    const [loaded, setLoaded] = useState(false);
    return (
        <div className="relative w-full max-h-[450px] aspect-video overflow-hidden rounded-[20px] md:rounded-[40px]">
            {!loaded && (
                <ImageSkeleton className={`absolute inset-0 w-full h-full ${rounded}`} />
            )}
            <Image
                src={src}
                alt={alt}
                fill
                priority
                onLoad={() => setLoaded(true)}
                className={`object-cover object-center rounded-[20px] md:rounded-[40px] transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            />
        </div>
    );
};

const MediaSlider = ({ media, title }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef(null);

    if (!media || media.length === 0) return null;

    if (media.length === 1) {
        return (
            <div className="mb-[40px]">
                <MediaImageWithSkeleton src={media[0]} alt={title} rounded="rounded-[20px] md:rounded-[40px]" />
            </div>
        );
    }

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        beforeChange: (_, next) => setCurrentSlide(next),
    };

    return (
        <div className="relative mb-[40px]">
            <Slider ref={sliderRef} {...settings}>
                {media.map((src, index) => (
                    <div key={index}>
                        <MediaImageWithSkeleton
                            src={src}
                            alt={`${title} - ${index + 1}`}
                            rounded="rounded-[20px] md:rounded-[40px]"
                        />
                    </div>
                ))}
            </Slider>

            <button
                onClick={() => sliderRef.current?.slickPrev()}
                className="absolute left-[16px] top-1/2 -translate-y-1/2 z-10 w-[40px] h-[40px] bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-colors"
            >
                <IoIosArrowBack size={20} className="text-[#091F59]" />
            </button>
            <button
                onClick={() => sliderRef.current?.slickNext()}
                className="absolute right-[16px] top-1/2 -translate-y-1/2 z-10 w-[40px] h-[40px] bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-colors"
            >
                <IoIosArrowForward size={20} className="text-[#091F59]" />
            </button>

            <div className="flex items-center justify-center gap-[8px] mt-[16px]">
                {media.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => sliderRef.current?.slickGoTo(index)}
                        className={`w-[10px] h-[10px] rounded-full transition-all duration-300 ${
                            currentSlide === index
                                ? 'bg-[#091F59] w-[24px]'
                                : 'bg-[#D1D5DB]'
                        }`}
                    />
                ))}
            </div>
        </div>
    );
};

const BlogDetail = ({ blog, similarBlogs }) => {
    if (!blog) return null;

    return (
        <Layout>
            <Head>
                <meta charSet="UTF-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <title>{blog.meta_title ?? `${blog.title} - PeerStack`}</title>
                <meta name="keywords" content="peerstack tech, blog" />
                <meta name="description" content={blog.meta_description ?? blog.sections?.[0]?.content ?? blog.title} />
                <meta name="image" content={blog.og_image ?? blog.media?.[0] ?? META_IMAGE} />
                <link rel="canonical" href={`${SITE_URL}/blogs/${blog.id}`} />
                <meta property="og:site_name" content="peerstack.tech" />
                <meta property="og:title" content={blog.meta_title ?? blog.title} />
                <meta property="og:url" content={`${SITE_URL}/blogs/${blog.id}`} />
                <meta property="og:description" content={blog.meta_description ?? blog.sections?.[0]?.content ?? blog.title} />
                <meta property="og:type" content="article" />
                <meta property="og:image" content={blog.og_image ?? blog.media?.[0] ?? META_IMAGE} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blog.meta_title ?? blog.title} />
                <meta name="twitter:url" content={`${SITE_URL}/blogs/${blog.id}`} />
                <meta name="twitter:description" content={blog.meta_description ?? blog.sections?.[0]?.content ?? blog.title} />
                <meta name="twitter:image" content={blog.og_image ?? blog.media?.[0] ?? META_IMAGE} />
            </Head>

            <div className="max-w-[90%] mx-auto pt-[40px] pb-[100px] lg:py-[100px]">
                <div className="max-w-full md:max-w-[80%] mx-auto">
                    <h1 className="text-[32px] md:text-[48px] lg:text-[60px] font-[500] mb-[32px] leading-tight text-center text-[#091F59]">
                        {blog.title}
                    </h1>

                    <MediaSlider media={blog.media} title={blog.title} />

                    <div className="max-w-[800px] mx-auto space-y-[40px]">
                        {blog.sections && blog.sections.length > 0 ? (
                            blog.sections.map((section, index) => (
                                <div key={index} className="space-y-[16px]">
                                    {section.header && (
                                        <h2 className="text-[22px] font-[600] text-[#222] mb-[30px]">
                                            {section.header}
                                        </h2>
                                    )}
                                    {section.content && (
                                        <p className="text-[16px] leading-relaxed text-[#222] whitespace-pre-line">
                                            {normalizeSectionContent(section.content)}
                                        </p>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="space-y-[16px]">
                                <p className="text-[16px] md:text-[18px] leading-relaxed text-[#222]">
                                    {blog.title}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <SimilarBlogs blogs={similarBlogs} />
            <WhatsappButton />
        </Layout>
    );
};

export default BlogDetail;
